import { useCallback, useEffect, useRef, type RefCallback } from 'react';

/** What `parseValue` managed to pull out of an author-written figure. */
interface ParsedValue {
  /** Anything before the digits — a currency symbol, say. */
  prefix: string;
  target: number;
  /** Anything after the digits: `+`, `%`, ` days`. */
  suffix: string;
  /** Decimal places to hold steady while counting, so `4.8` never renders as `4`. */
  decimals: number;
  /** Whether the author grouped thousands, so `1,200` does not count up as `1200`. */
  grouped: boolean;
}

/**
 * Greedy leading non-digits, then the number, then the rest.
 *
 * `[\d.,]*\d` rather than `[\d.,]+` so the number cannot end on a separator — for
 * `"14 days"` the digits stop at `14`, and a trailing `.` in a sentence stays in the
 * suffix instead of being parsed as a decimal point.
 */
const NUMBER_PATTERN = /^(\D*)([\d.,]*\d)(.*)$/s;

function parseValue(value: string): ParsedValue | null {
  const match = NUMBER_PATTERN.exec(value);
  if (match === null) return null;

  const [, prefix = '', digits = '', suffix = ''] = match;
  const plain = digits.replace(/,/g, '');
  const point = plain.indexOf('.');
  const target = Number(plain);

  // `Number('1.2.3')` is NaN, which is the case this guard is really for.
  if (!Number.isFinite(target)) return null;

  return {
    prefix,
    target,
    suffix,
    decimals: point === -1 ? 0 : plain.length - point - 1,
    grouped: digits.includes(','),
  };
}

function formatValue(current: number, parsed: ParsedValue): string {
  const body = parsed.grouped
    ? current.toLocaleString('en-US', {
        minimumFractionDigits: parsed.decimals,
        maximumFractionDigits: parsed.decimals,
      })
    : current.toFixed(parsed.decimals);

  return `${parsed.prefix}${body}${parsed.suffix}`;
}

/**
 * Counts a figure up from zero when it scrolls into view.
 *
 * Returns a ref callback for the element holding the number. That element must already
 * render the final figure as its own text: this hook only *replaces* the text, so a
 * browser without `IntersectionObserver`, a reader who asked for reduced motion, and a
 * value with no digits in it (`"—"`, the honest default before the client supplies real
 * numbers) all end up showing the figure as written, with no animation and no blank.
 *
 * The count is written straight to `textContent` rather than held in state. At 60fps a
 * four-figure band would otherwise re-render React roughly 340 times over the course of
 * one animation, to change four leaf text nodes.
 *
 * Because the text is overwritten in place, the element it is attached to should be
 * `aria-hidden`, with the real figure exposed to assistive technology separately —
 * otherwise a screen reader that reaches the band mid-count reads out a number that was
 * never true.
 */
export function useCountUp<T extends HTMLElement = HTMLSpanElement>(
  value: string,
  durationMs = 1400,
): RefCallback<T> {
  /**
   * Teardown for whatever the last attached node started.
   *
   * Held in a ref so unmount can reach it: a ref callback's return value is only
   * honoured by React 19+, and relying on that alone would leak an observer and a
   * pending frame on anything older.
   */
  const teardown = useRef<(() => void) | null>(null);

  useEffect(
    () => () => {
      teardown.current?.();
      teardown.current = null;
    },
    [],
  );

  return useCallback(
    (node: T | null) => {
      teardown.current?.();
      teardown.current = null;

      if (node === null) return;
      if (typeof IntersectionObserver === 'undefined') return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      const parsed = parseValue(value);
      if (parsed === null) return;

      // Only now is the animation certain to run, so this is the last moment at which
      // it is safe to blank out the figure the server rendered.
      node.textContent = formatValue(0, parsed);

      let frame = 0;
      let startedAt: number | null = null;

      const step = (now: number): void => {
        startedAt ??= now;
        const progress = Math.min((now - startedAt) / durationMs, 1);

        if (progress >= 1) {
          // Land on the author's own string rather than our formatting of it, so any
          // spacing or separator style we failed to model survives the animation.
          node.textContent = value;
          frame = 0;
          return;
        }

        // easeOutCubic: most of the distance covered early, then a settle. A linear
        // count reads like a loading spinner rather than a figure arriving.
        node.textContent = formatValue(parsed.target * (1 - (1 - progress) ** 3), parsed);
        frame = requestAnimationFrame(step);
      };

      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            // One-shot, like `useReveal`: scrolling back past does not re-count.
            observer.disconnect();
            frame = requestAnimationFrame(step);
          }
        },
        // Higher than `useReveal`'s tenth: a figure that starts counting while barely
        // on screen has finished by the time it is properly readable.
        { threshold: 0.4 },
      );

      observer.observe(node);

      teardown.current = () => {
        observer.disconnect();
        if (frame !== 0) cancelAnimationFrame(frame);
        node.textContent = value;
      };
    },
    [value, durationMs],
  );
}
